import { markets } from '$lib/home/store';
import { StrategyIds, type FeeInfo } from '$lib/types/strategy';
import type { Market } from '$lib/types/market';
import type {
	StartergyPostTradeDetails,
	StrategyPosition,
	StrategyType
} from '$lib/types/strategy';
import BigNumber from 'bignumber.js';
import { writable, type Readable, type Writable, derived } from 'svelte/store';
import type { IMatchaResponse } from '$lib/web3/types';

export const currentStrategyIdentifier: Writable<StrategyIds> = writable(StrategyIds.ETH);
export const strategyDetails: Writable<Array<StrategyType>> = writable([]);
export const strategyPositions: Writable<Array<StrategyPosition>> = writable([]);
export const firstTimeFetchingStrategyPositions: Writable<boolean> = writable(true);
export const loadingTradeDetails: Writable<boolean> = writable(false);
export const collateralInputForStartergy: Writable<string> = writable('0');
export const sizeForStartergy: Writable<string> = writable('0');
export const isExecutingStrategy: Writable<boolean> = writable(false);
export const strategyLongSize: Writable<string> = writable('0');
export const strategyShortSize: Writable<string> = writable('0');
export const strategyLongMargin: Writable<string> = writable('0');
export const strategyShortMargin: Writable<string> = writable('0');
export const strategyPostTradeDetails: Writable<StartergyPostTradeDetails | null> = writable();
export const fetchingStrategyPostTradeDetails: Writable<boolean> = writable(false);
export const startergyLongLiquidationPrice: Writable<string> = writable('0');
export const stratergyShortLiquidationPrice: Writable<string> = writable('0');

export const aaveCollateral: Writable<string> = writable('0');
export const aaveDebt: Writable<string> = writable('0');
export const fxMarginDelta: Writable<string> = writable('0');
export const fxSizeDelta: Writable<string> = writable('0');

export const isAdvanced: Writable<boolean> = writable(false);
export const strategySwapMatchaCallData: Writable<IMatchaResponse | null> = writable();
export const fetchingStrategySwapMatchaCallData: Writable<boolean> = writable(false);

export const currentStrategy: Readable<StrategyType> = derived(
	[strategyDetails, currentStrategyIdentifier],
	([$strategyDetails, $currentStrategyIdentifier]) => {
		return $strategyDetails.find((e) => e.id == $currentStrategyIdentifier) || $strategyDetails[0];
	}
);

export const isStrategyPositionOpen: Readable<boolean> = derived(
	[strategyPositions],
	([$strategyPositions]) => {
		if ($strategyPositions.length > 0) {
			return new BigNumber($strategyPositions[0]?.long?.currentSupplySizeUnderlying).gt(0);
		} else {
			return false;
		}
	}
);

export const finalStrategyLongSize: Readable<string> = derived(
	[strategyLongSize, isAdvanced, sizeForStartergy],
	([$strategyLongSize, $isAdvanced, $sizeForStartergy]) => {
		if ($isAdvanced) {
			return $strategyLongSize;
		} else if ($sizeForStartergy) {
			return new BigNumber($sizeForStartergy).div(2).toFixed(0);
		} else {
			return '0';
		}
	}
);

export const finalStrategyShortSize: Readable<string> = derived(
	[sizeForStartergy],
	([$sizeForStartergy]) => {
		if ($sizeForStartergy) {
			return new BigNumber($sizeForStartergy).div(2).multipliedBy(-1).toFixed(0);
		} else {
			return '0';
		}
	}
);

export const finalMarginLongSize: Readable<string> = derived(
	[strategyLongMargin, isAdvanced, collateralInputForStartergy],
	([$strategyLongMargin, $isAdvanced, $collateralInputForStartergy]) => {
		if ($isAdvanced) {
			return $strategyLongMargin;
		} else if ($collateralInputForStartergy) {
			return new BigNumber($collateralInputForStartergy).div(2).toFixed(0);
		} else {
			return '0';
		}
	}
);

export const finalMarginShortSize: Readable<string> = derived(
	[strategyShortMargin, isAdvanced, collateralInputForStartergy],
	([$strategyShortMargin, $isAdvanced, $collateralInputForStartergy]) => {
		if ($isAdvanced) {
			return $strategyShortMargin;
		} else if ($collateralInputForStartergy) {
			return new BigNumber($collateralInputForStartergy).div(2).toFixed(0);
		} else {
			return '0';
		}
	}
);

export const strategyMarket: Readable<Market | undefined> = derived(
	[markets, currentStrategy],
	([$markets, $currentStrategy]) => {
		if ($markets && $markets.length > 0 && $currentStrategy?.marketIdentifier) {
			// return the market with the same identifier as the current market
			return $markets.find((e) => e.marketIdentifier == $currentStrategy.marketIdentifier);
		} else {
			return undefined;
		}
	}
);

export const stratergyLeverage: Readable<number> = derived(
	[currentStrategy, sizeForStartergy, collateralInputForStartergy, strategyMarket],
	([$currentStrategy, $sizeForStartergy, $collateralInputForStartergy, $strategyMarket]) => {
		const hasInvalidInputs =
			!$currentStrategy ||
			new BigNumber($sizeForStartergy).isEqualTo(new BigNumber(0)) ||
			new BigNumber($collateralInputForStartergy).isEqualTo(new BigNumber(0)) ||
			!$strategyMarket?.currentOraclePrice;

		if (hasInvalidInputs) return 0;
		const leverage = new BigNumber($sizeForStartergy)
			.multipliedBy($strategyMarket.currentOraclePrice)
			.dividedBy(new BigNumber($collateralInputForStartergy))
			.div(1e18)
			.toFixed(2);
		return Number(leverage);
	}
);

export const minSizeAmount: Writable<string> = writable('0000000000000000000');
// Readable<string> = derived(
// 	[currentStrategy, collateralInputForStartergy, strategyMarket],
// 	([$currentStrategy, $collateralInputForStartergy, $strategyMarket]) => {
// 		if (!$currentStrategy || !$strategyMarket?.currentOraclePrice) return '0';
// 		return new BigNumber(1.00000000000000000001)
// 			.multipliedBy(new BigNumber($collateralInputForStartergy))
// 			.div($strategyMarket.currentOraclePrice)
// 			.multipliedBy(1e18)
// 			.toFixed(0);
// 	}
// );

export const maxSizeAmount: Readable<string> = derived(
	[currentStrategy, collateralInputForStartergy, strategyMarket],
	([$currentStrategy, $collateralInputForStartergy, $strategyMarket]) => {
		if (!$currentStrategy || !$strategyMarket?.currentOraclePrice) return '0';
		return new BigNumber($currentStrategy.long.maxLeveragePossible)
			.minus(0.1) // decrease max leverage by 0.1 to account for rounding errors
			.multipliedBy(new BigNumber($collateralInputForStartergy))
			.div($strategyMarket.currentOraclePrice)
			.multipliedBy(1e18)
			.toFixed(0);
	}
);

export const feeInfo: Readable<FeeInfo> = derived(
	strategyPostTradeDetails,
	($strategyPostTradeDetails) => {
		if (!$strategyPostTradeDetails)
			return {
				totalCost: '0',
				AaveFlashLoanFee: '0',
				AaveSwapFee: '0',
				PolynomialFee: '0'
			};

		const aaveFlashLoanFee = new BigNumber(
			$strategyPostTradeDetails.long.postTradeDetails.flashLoanFeeUSD
		).gte(0)
			? $strategyPostTradeDetails.long.postTradeDetails.flashLoanFeeUSD
			: '0';

		return {
			totalCost: new BigNumber($strategyPostTradeDetails.long.postTradeDetails.flashLoanFeeUSD)
				.plus($strategyPostTradeDetails.long.postTradeDetails.swapFeeUSD)
				.plus($strategyPostTradeDetails.short.fee)
				.toString(),
			AaveFlashLoanFee: aaveFlashLoanFee,
			AaveSwapFee: $strategyPostTradeDetails.long.postTradeDetails.swapFeeUSD,
			PolynomialFee: $strategyPostTradeDetails.short.fee
		};
	}
);
